/**
 * Runtime polyfills for Yandex / older Chromium (TC39 Upsert, Promise.try,
 * Promise.withResolvers, Math.sumPrecise) and older iOS Safari (<18.2 Uint8Array
 * hex/base64; <17.4 ReadableStream async iteration).
 *
 * pdfjs v6 uses several of these (notably Promise.withResolvers and
 * Math.sumPrecise) internally on both the main thread and inside its worker.
 * A Web Worker has its own global scope, so the shim must run in BOTH contexts.
 *
 * This file covers the MAIN THREAD — import it first in the app entry (main.tsx),
 * before any other module (incl. pdfjs) is evaluated. The pdfjs worker is loaded
 * verbatim from node_modules via `?url`, so its copy of these polyfills is
 * injected at the top of pdf.worker.mjs by patches/pdfjs-dist+6.0.227.patch.
 * Keep the two in sync.
 */

declare global {
  interface Math {
    sumPrecise(values: Iterable<number>): number;
  }
  interface PromiseConstructor {
    try<T, A extends readonly unknown[]>(
      callbackFn: (...args: A) => T | PromiseLike<T>,
      ...args: A
    ): Promise<Awaited<T>>;
    withResolvers<T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    };
  }
  interface Map<K, V> {
    getOrInsert(key: K, value: V): V;
    getOrInsertComputed(key: K, callbackfn: (key: K) => V): V;
  }
  interface WeakMap<K extends WeakKey, V> {
    getOrInsertComputed(key: K, callbackfn: (key: K) => V): V;
  }
  interface Uint8Array {
    toHex(): string;
    toBase64(): string;
  }
  interface Uint8ArrayConstructor {
    fromBase64(string: string): Uint8Array;
  }
}

if (typeof Math.sumPrecise !== 'function') {
  Math.sumPrecise = function sumPrecise(values: Iterable<number>): number {
    if (values == null || typeof values[Symbol.iterator] !== 'function') {
      throw new TypeError('Math.sumPrecise requires an iterable');
    }

    // Maintains a list of non-overlapping partial sums whose
    // exact total equals the exact total of everything seen so far.
    const partials: number[] = [];

    let hasNaN = false;
    let posInf = false;
    let negInf = false;

    for (const value of values) {
      if (typeof value !== 'number') {
        throw new TypeError('Math.sumPrecise requires all elements to be numbers');
      }

      if (value !== value) {
        hasNaN = true;
        continue;
      }
      if (value === Infinity) {
        posInf = true;
        continue;
      }
      if (value === -Infinity) {
        negInf = true;
        continue;
      }
      if (value === 0) continue; // +0 and -0 contribute nothing to the sum

      // Shewchuk / Neumaier: fold `value` into the running set of partials
      let x = value;
      let i = 0;
      for (let j = 0; j < partials.length; j++) {
        let y = partials[j];
        if (Math.abs(x) < Math.abs(y)) {
          const t = x;
          x = y;
          y = t;
        }
        const hi = x + y;
        const lo = y - (hi - x); // exact rounding error of the addition
        if (lo !== 0) partials[i++] = lo;
        x = hi;
      }
      partials.length = i;
      partials.push(x);
    }

    // Special-value resolution, matching the spec's precedence
    if (hasNaN) return NaN;
    if (posInf && negInf) return NaN;
    if (posInf) return Infinity;
    if (negInf) return -Infinity;

    // Empty / all-zero sum returns -0 per spec
    if (partials.length === 0) return -0;

    // Sum the (non-overlapping, ascending-magnitude) partials from smallest
    // to largest so the final rounding lands on the correctly-rounded result.
    let result = 0;
    for (let i = 0; i < partials.length; i++) {
      result += partials[i];
    }
    return result;
  };
}

if (typeof Map.prototype.getOrInsertComputed !== 'function') {
  Map.prototype.getOrInsertComputed = function (
    this: Map<unknown, unknown>,
    key: unknown,
    callbackfn: (key: unknown) => unknown,
  ): unknown {
    if (this.has(key)) return this.get(key);
    const value = callbackfn(key);
    this.set(key, value);
    return value;
  };
}

if (typeof Map.prototype.getOrInsert !== 'function') {
  Map.prototype.getOrInsert = function (this: Map<unknown, unknown>, key: unknown, value: unknown): unknown {
    if (this.has(key)) return this.get(key);
    this.set(key, value);
    return value;
  };
}

if (typeof WeakMap.prototype.getOrInsertComputed !== 'function') {
  WeakMap.prototype.getOrInsertComputed = function (
    this: WeakMap<WeakKey, unknown>,
    key: WeakKey,
    callbackfn: (key: WeakKey) => unknown,
  ): unknown {
    if (this.has(key)) return this.get(key);
    const value = callbackfn(key);
    this.set(key, value);
    return value;
  };
}

if (typeof Promise.try !== 'function') {
  Promise.try = function <T, A extends readonly unknown[]>(
    callbackFn: (...args: A) => T | PromiseLike<T>,
    ...args: A
  ): Promise<Awaited<T>> {
    return new Promise<Awaited<T>>((resolve) => {
      resolve(callbackFn(...args) as Awaited<T>);
    });
  };
}

if (typeof Promise.withResolvers !== 'function') {
  Promise.withResolvers = function <T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

if (typeof Uint8Array.prototype.toHex !== 'function') {
  Uint8Array.prototype.toHex = function (this: Uint8Array): string {
    let hex = '';
    for (let i = 0; i < this.length; i++) {
      hex += this[i].toString(16).padStart(2, '0');
    }
    return hex;
  };
}

if (typeof Uint8Array.prototype.toBase64 !== 'function') {
  Uint8Array.prototype.toBase64 = function (this: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < this.length; i++) {
      binary += String.fromCharCode(this[i]);
    }
    return btoa(binary);
  };
}

if (typeof Uint8Array.fromBase64 !== 'function') {
  Uint8Array.fromBase64 = (string: string): Uint8Array => {
    const binary = atob(string);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };
}

// ReadableStream is declared generically as `ReadableStream<R = any>` in the DOM
// lib; augmenting it globally would force an `any` default into our source. Patch
// the prototype through a local structural cast instead.
interface ReadableStreamAsyncIterable {
  values?: (options?: { preventCancel?: boolean }) => AsyncIterableIterator<unknown>;
  [Symbol.asyncIterator]?: (options?: { preventCancel?: boolean }) => AsyncIterableIterator<unknown>;
}

const streamProto = ReadableStream.prototype as unknown as ReadableStreamAsyncIterable;

if (typeof streamProto[Symbol.asyncIterator] !== 'function') {
  const values = function (
    this: ReadableStream<unknown>,
    { preventCancel = false }: { preventCancel?: boolean } = {},
  ): AsyncIterableIterator<unknown> {
    const reader = this.getReader();
    const iterator = {
      next() {
        return reader.read().then(
          (result) => {
            if (result.done) reader.releaseLock();
            return result as IteratorResult<unknown>;
          },
          (reason) => {
            reader.releaseLock();
            throw reason;
          },
        );
      },
      return(value?: unknown) {
        if (!preventCancel) {
          const cancelPromise = reader.cancel(value);
          reader.releaseLock();
          return cancelPromise.then(() => ({ value, done: true as const }));
        }
        reader.releaseLock();
        return Promise.resolve({ value, done: true as const });
      },
      [Symbol.asyncIterator]() {
        return this;
      },
    };
    return iterator as unknown as AsyncIterableIterator<unknown>;
  };

  streamProto.values = values;
  streamProto[Symbol.asyncIterator] = values;
}

export {};
